import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { ArrowRightStartOnRectangleIcon, BellIcon, Bars4Icon } from '@heroicons/react/24/outline';
import styles from './AdminHeader.module.css';
import { useLocation } from "react-router-dom";
import PushPopUp from "../../../views/client/popup/PushPopUp";
import { checkNotificationAPI, getNotificationAPI } from '../../../services/notification';
import { NotificationResponseData } from "../../../types/notification";
import { handleLogout } from "../../../services/base";

interface HeaderProps {
    className?: string;
    isSidebarExpanded?: boolean;
    toggleSidebar?: () => void;
}

// 주소별 헤더 출력 제목 (상수로 분리)
const pathTitles: Record<string, { title: string; exact?: boolean }> = {
    '/admin': { title: 'Hello, Admin', exact: true },
    '/admin/projects': { title: 'Projects' },
    '/admin/clients': { title: 'Clients' },
    '/admin/documents': { title: 'Documents' },
    '/admin/billing': { title: 'Billing' },
    '/admin/settings': { title: 'Settings' }
} as const;

const NOTIFICATION_REFRESH_INTERVAL = 60000; // 1분

const AdminHeader: React.FC<HeaderProps> = ({
                                                className = '',
                                                isSidebarExpanded,
                                                toggleSidebar
                                            }) => {
    const location = useLocation();
    const [showNotifications, setShowNotifications] = useState(false);
    const [notifications, setNotifications] = useState<NotificationResponseData[]>([]);
    const [notificationCount, setNotificationCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    // 알림 가져오기
    const fetchNotifications = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await getNotificationAPI();
            setNotifications(data);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // 읽지 않은 알림 개수 확인
    const checkNotifications = useCallback(async () => {
        try {
            const count = await checkNotificationAPI();
            setNotificationCount(count);
        } catch (error) {
            console.error('Failed to check notifications:', error);
        }
    }, []);

    // 알림 새로고침
    const refreshNotifications = useCallback(async () => {
        await Promise.all([fetchNotifications(), checkNotifications()]);
    }, [fetchNotifications, checkNotifications]);

    // 초기 로드 및 주기적 새로고침
    useEffect(() => {
        refreshNotifications();

        const interval = setInterval(refreshNotifications, NOTIFICATION_REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, [refreshNotifications]);

    // 페이지별 헤더 제목 계산
    const headerTitle = useMemo(() => {
        // 정확한 경로 매치 확인
        const exactMatch = Object.entries(pathTitles).find(([path, config]) =>
            config.exact && location.pathname === path
        );
        if (exactMatch) return exactMatch[1].title;

        // 시작 경로 매치 확인
        const startMatch = Object.entries(pathTitles).find(([path, config]) =>
            !config.exact && location.pathname.startsWith(path)
        );
        return startMatch ? startMatch[1].title : 'Dashboard';
    }, [location.pathname]);

    // 알림 팝업 닫기 핸들러
    const handleNotificationClose = useCallback(() => {
        setShowNotifications(false);
        checkNotifications();
    }, [checkNotifications]);

    // 알림 업데이트 핸들러
    const handleNotificationUpdate = useCallback(() => {
        refreshNotifications();
    }, [refreshNotifications]);

    // 알림 배지 텍스트 계산
    const notificationBadgeText = useMemo(() => {
        return notificationCount > 99 ? '99+' : notificationCount.toString();
    }, [notificationCount]);

    return (
        <>
            <header className={`${styles.header} ${className}`}>
                {/* 헤더 제목 */}
                <div className={styles.titleSection}>
                    {location.pathname === '/admin' ? (
                        <h1 className={styles.greeting}>
                            {headerTitle}
                        </h1>
                    ) : (
                        <h1>{headerTitle}</h1>
                    )}
                </div>

                <div className={styles.actions}>
                    <div className={styles.notificationWrapper}>
                        {/* 푸시 알림 팝업 버튼 */}
                        <button
                            className={styles.iconButton}
                            onClick={() => setShowNotifications(true)}
                            disabled={isLoading}
                            aria-label={`알림 ${notificationCount}개`}
                        >
                            <BellIcon className={styles.icon} />
                        </button>
                        {/* 안읽은 푸시 알림 개수 */}
                        {notificationCount > 0 && (
                            <span className={styles.notificationBadge}>
                                {notificationBadgeText}
                            </span>
                        )}
                    </div>

                    {/* 로그아웃 버튼 */}
                    <button
                        className={styles.iconButton}
                        onClick={handleLogout}
                        aria-label="로그아웃"
                    >
                        <ArrowRightStartOnRectangleIcon className={styles.icon} />
                    </button>

                    {/* 반응형 메뉴 버튼 */}
                    <button
                        className={styles.mobileButton}
                        onClick={toggleSidebar}
                        aria-label="메뉴 토글"
                    >
                        <Bars4Icon className={styles.icon} />
                    </button>
                </div>
            </header>

            {/* 푸시 알림 팝업 */}
            {showNotifications && (
                <PushPopUp
                    notifications={notifications}
                    onClose={handleNotificationClose}
                    onUpdate={handleNotificationUpdate}
                />
            )}
        </>
    );
};

export default AdminHeader;